import React from 'react';
import { copyFor } from 'config';
import { formatAsMoney } from '@perch-labs/warbler';
import { Badge, Crate, FlexCrate, Icon, Tooltip } from '@perch-labs/shrike';
import {
  StyledListItem,
  StyledContainer,
  StyledHeading,
  StyledSupportingText,
  StyledText,
  StyledTooltipContainer,
} from '../line-items.style';
import { ArrowIcon } from '../arrow-icon';
import { LineItemAmount } from '../line-item-amount.component';

interface Props {
  lineItem: SolarLineItem;
  includeTooltips: boolean;
  includeSupportingText: boolean;
  utilityName?: string;
}

const getCopy = copyFor('billing.statement.lineItems.vnmSolarCharge');

export const VnmSolarChargeLineItem = ({
  lineItem,
  includeTooltips,
  includeSupportingText,
  utilityName,
}: Props) => {
  const { amountCents, savings, beforeSavings, savingsPercentage } = lineItem;
  const project = lineItem.vnmSolarProject?.name;

  if (amountCents === 0) {
    return null;
  }

  const beforeSavingsAsMoney = formatAsMoney(beforeSavings, {
    conversion: 'fromCents',
  });
  const savingsAsMoney = formatAsMoney(savings * -1, {
    conversion: 'fromCents',
  });

  const tooltipCopy = getCopy('tooltip.copy', {
    utilityName,
    amount: beforeSavingsAsMoney,
  });
  const savingsTooltipCopy = getCopy('savings.tooltip.copy', {
    utilityName,
    savingsPercentage: savingsPercentage * 100,
  });
  const subtotalTooltipCopy = getCopy('subtotal.tooltip.copy', {
    utilityName,
  });

  return (
    <StyledListItem data-testid="vnm-solar-charge-line-item">
      <StyledContainer>
        <FlexCrate flexDirection="column" gap="8px">
          <StyledTooltipContainer>
            <StyledHeading>
              {getCopy('title')}
              {project ? ` from ${project}` : ''}
            </StyledHeading>
            {includeTooltips && (
              <Tooltip
                content={tooltipCopy}
                tooltipId="vnmSolarCharge"
                label={getCopy('tooltip.label')}
              >
                <Icon icon="InfoCircleInverted" />
              </Tooltip>
            )}
          </StyledTooltipContainer>
          {includeSupportingText && (
            <Crate padding="0 64px 0 0">
              <StyledSupportingText>{tooltipCopy}</StyledSupportingText>
            </Crate>
          )}
        </FlexCrate>

        <StyledText lineThrough={true}>{beforeSavingsAsMoney}</StyledText>
      </StyledContainer>

      <FlexCrate
        flexDirection="column"
        margin={{ top: '16px' }}
        gap="16px"
        width="100%"
      >
        <StyledContainer>
          <FlexCrate flexDirection="column" gap="8px">
            <StyledTooltipContainer>
              <StyledText>
                {' '}
                <ArrowIcon />
                {includeSupportingText ? (
                  <b>{getCopy('savings.title')}</b>
                ) : (
                  getCopy('savings.title')
                )}
              </StyledText>
              {includeTooltips && (
                <Tooltip
                  content={savingsTooltipCopy}
                  tooltipId="vnmSolarChargeSavings"
                  label={getCopy('savings.tooltip.label')}
                >
                  <Icon icon="InfoCircleInverted" />
                </Tooltip>
              )}
            </StyledTooltipContainer>
            {includeSupportingText && (
              <Crate padding="0 64px 0 32px">
                <StyledSupportingText>
                  {savingsTooltipCopy}
                </StyledSupportingText>
              </Crate>
            )}
          </FlexCrate>
          <Badge backgroundColor="accent3" margin={{ right: '-8px' }}>
            {savingsAsMoney}
          </Badge>
        </StyledContainer>

        <StyledContainer>
          <FlexCrate flexDirection="column" gap="8px">
            <StyledTooltipContainer>
              <StyledText>
                {' '}
                <ArrowIcon />
                {includeSupportingText ? (
                  <b>{getCopy('subtotal.title')}</b>
                ) : (
                  getCopy('subtotal.title')
                )}
              </StyledText>
              {includeTooltips && (
                <Tooltip
                  content={subtotalTooltipCopy}
                  tooltipId="vnmSolarChargeSubtotal"
                  label={getCopy('subtotal.tooltip.label')}
                >
                  <Icon icon="InfoCircleInverted" />
                </Tooltip>
              )}
            </StyledTooltipContainer>
            {includeSupportingText && (
              <Crate padding="0 64px 0 32px">
                <StyledSupportingText>
                  {subtotalTooltipCopy}
                </StyledSupportingText>
              </Crate>
            )}
          </FlexCrate>
          <LineItemAmount amountCents={amountCents} />
        </StyledContainer>
      </FlexCrate>
    </StyledListItem>
  );
};
